import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import contexts
import { AuthProvider } from './contexts/AuthContext';

// Import components
import ProtectedRoute from './components/ProtectedRoute';

// Import pages
import ChatPage from './pages/ChatPage';
import SharedConversationPage from './pages/SharedConversationPage';
import AdminDashboard from './pages/admin/Dashboard';
import ContentSources from './pages/admin/ContentSources';
import Prompts from './pages/admin/Prompts';
import Teaching from './pages/admin/Teaching';
import WidgetIntegrations from './pages/admin/WidgetIntegrations';
import SystemLogs from './pages/admin/SystemLogs';
import UserManagement from './pages/admin/UserManagement';
import AdminLayout from './components/AdminLayout';
import DomainContentManager from './components/DomainContentManager';

// Create a client
const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Default route - redirect to chat */}
            <Route path="/" element={<Navigate to="/chat" replace />} />
            
            {/* Chat interface - open to everyone for now */}
            <Route path="/chat" element={<ChatPage />} />
            
            {/* Domain Content Management - admin protected */}
            <Route path="/admin/domain-content" element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout>
                  <DomainContentManager />
                </AdminLayout>
              </ProtectedRoute>
            } />

            {/* Shared conversation view - public access */}
            <Route path="/shared/:token" element={<SharedConversationPage />} />

            {/* Admin routes with layout - require admin role */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </ProtectedRoute>
            } />
          
          <Route path="/admin/dashboard" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          {/* Redirect old activity-logs URL to consolidated system logs */}
          <Route path="/admin/activity-logs" element={
            <Navigate to="/admin/logs" replace />
          } />
          
          {/* Content Sources Admin Page - Owner only */}
          <Route path="/admin/content-sources" element={
            <ProtectedRoute requiredCapability="canManageContentSources">
              <AdminLayout>
                <ContentSources />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/prompts" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <Prompts />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/widgets" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <WidgetIntegrations />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/teaching" element={
            <ProtectedRoute requiredCapability="canUseTeaching">
              <AdminLayout>
                <Teaching />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/users" element={
            <ProtectedRoute requiredCapability="canManageUsers">
              <AdminLayout>
                <UserManagement />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/logs" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <SystemLogs />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/chat" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </QueryClientProvider>
  );
}

// Coming Soon Component - kept for future pages under development
const _ComingSoonPage: React.FC<{ title: string }> = ({ title }) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">{title}</h2>
      <p className="text-gray-600 mb-4">This page is currently being developed.</p>
      <p className="text-sm text-gray-500">
        We're migrating the legacy admin interface to this modern React interface.
      </p>
    </div>
  </div>
);
void _ComingSoonPage; // Available for future placeholder pages

export default App;